import { useState } from 'react';
import type { TrunkLive } from '@arcnet/contracts';
import { StatusBadge, Stat, StatGroup, Segmented } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Sparkline, UptimeStrip } from '../../components/charts/Charts.js';
import { useTrunkHistory } from '../../lib/api/hooks-products.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';

/**
 * A trunk's live state and its record, as the switch measured them.
 *
 * Health is an OPTIONS ping OpenSIPS sends the carrier from our SIP address --
 * any reply counts, a 401 included; only silence counts against it. Latency is
 * a direct ping's round trip. Registration is OpenSIPS's own record. All of it
 * comes from the switch (arc-agent trunkstatus.ts) and is laid over the trunk
 * by the API; the record from ns_trunk_samples and ns_trunk_events.
 *
 * The words are StatusBadge's -- up, degraded, down, unknown; registered,
 * pending, unregistered -- so a trunk reads the way everything else does.
 * "No report" is unknown, never down: a switch that has stopped reporting
 * knows nothing about the carrier.
 */

/** Matches the API's LIVE_STALE_MS: past this the switch has stopped reporting. */
const STALE_MS = 120_000;

const fresh = (live: TrunkLive | null | undefined): live is TrunkLive =>
  !!live && Date.now() - Date.parse(live.reported_at) <= STALE_MS;

export const fmtMs = (ms: number): string => (ms < 10 ? `${ms.toFixed(1)} ms` : `${Math.round(ms)} ms`);

const health = (live: TrunkLive | null | undefined): string =>
  !fresh(live) || live.health === 'off' ? 'unknown' : live.health;

/**
 * A trunk's status and, when it is up, its latency. The one rendering of it:
 * staff tables feed it from live state, the customer's from their summary.
 */
export function TrunkHealth({ status, latencyMs }: { status: string; latencyMs?: number | null | undefined }) {
  return (
    <span className="arc-row" style={{ gap: 6, display: 'inline-flex' }}>
      <StatusBadge status={status} />
      {status === 'up' && latencyMs != null && (
        <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>{fmtMs(latencyMs)}</span>
      )}
    </span>
  );
}

export function HealthBadge({ live }: { live: TrunkLive | null | undefined }) {
  return <TrunkHealth status={health(live)} latencyMs={live?.latency_ms} />;
}

/**
 * Registration in StatusBadge's words; null for a trunk that does not
 * register, which shows a dash rather than a state it cannot be in.
 */
export function TrunkRegistration({ status, reason }: {
  status: 'registered' | 'pending' | 'unregistered' | 'unknown' | null; reason?: string | undefined;
}) {
  if (status === null) return <span className="arc-subtle" title={reason ?? 'This trunk does not register.'}>—</span>;
  return <span title={reason}><StatusBadge status={status} /></span>;
}

/** Registration as a StatusBadge word, with the exact reason on hover. */
function registration(live: TrunkLive | null | undefined, authMode: string | undefined): {
  status: 'registered' | 'pending' | 'unregistered' | 'unknown' | null; reason: string;
} {
  if (authMode && authMode !== 'register') return { status: null, reason: 'This trunk does not register.' };
  if (!fresh(live)) return { status: 'unknown', reason: 'No report from the switch.' };
  switch (live.reg_state) {
    case 'REGISTERED_STATE':
      return {
        status: 'registered',
        reason: live.last_register_at ? `Registered ${relativeTime(live.last_register_at)}` : 'Registered',
      };
    case 'WRONG_CREDENTIALS_STATE':
      return { status: 'unregistered', reason: 'The carrier refused the username, password or realm.' };
    case 'REGISTRAR_ERROR_STATE':
      return { status: 'unregistered', reason: 'The carrier answered with an error.' };
    case 'REGISTER_TIMEOUT_STATE':
      return { status: 'unregistered', reason: 'The carrier did not answer.' };
    case 'INTERNAL_ERROR_STATE':
      return { status: 'unregistered', reason: 'The switch could not send the registration.' };
    case null:
      return { status: 'unknown', reason: 'No report from the switch.' };
    default:
      return { status: 'pending', reason: 'Registering.' };
  }
}

export function RegistrationBadge({ live, authMode }: {
  live: TrunkLive | null | undefined; authMode: string | undefined;
}) {
  const r = registration(live, authMode);
  return <TrunkRegistration status={r.status} reason={r.reason} />;
}

/**
 * The editor's view: the four figures, the record, and the one fix the switch
 * can offer outright -- a realm that does not match the carrier's.
 *
 * The node holds only HA1 = MD5(user:realm:password). The realm is inside the
 * hash, so a realm off by one label fails every login with a password that is
 * right, and "wrong credentials" does not point at the realm. The switch reads
 * the realm out of the carrier's own challenge, so this can say so exactly.
 */
export function TrunkLivePanel({ live, authMode, realm, onUseRealm, trunkId, tenantId }: {
  live: TrunkLive | null | undefined;
  authMode: string;
  /** The realm in the form: its own, or the host when that is blank. */
  realm: string;
  onUseRealm: (realm: string) => void;
  trunkId: string;
  /** For a customer reading its own trunk, whose record comes through its account. */
  tenantId?: string | undefined;
}) {
  const reg = registration(live, authMode);
  const h = health(live);
  const mismatch = authMode !== 'ip' && live?.challenge_realm && live.challenge_realm !== realm
    ? live.challenge_realm : null;

  return (
    <div style={{ marginBottom: 16 }}>
      {mismatch && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} />
          <span style={{ flex: 1 }}>
            The carrier’s realm is <strong className="arc-mono">{mismatch}</strong>; this trunk
            uses <span className="arc-mono">{realm}</span>. Every login fails until they match.
          </span>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => onUseRealm(mismatch)}>
            Use {mismatch}
          </button>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <StatGroup>
          <Stat bare label="Carrier"
            value={h === 'unknown' ? '—' : h[0]!.toUpperCase() + h.slice(1)}
            tone={h === 'up' ? 'ok' : h === 'down' ? 'danger' : h === 'degraded' ? 'warn' : undefined}
            sub={!fresh(live) ? 'no report from the switch'
              : live.health === 'off' ? live.last_reply ?? 'not checked'
              : live.health_since ? `since ${relativeTime(live.health_since)}` : undefined} />
          <Stat bare label="Registration"
            value={reg.status === null ? '—' : reg.status[0]!.toUpperCase() + reg.status.slice(1)}
            tone={reg.status === 'registered' ? 'ok' : reg.status === 'unregistered' ? 'danger' : undefined}
            sub={reg.status === null ? 'not used' : reg.reason} />
          <Stat bare label="Latency"
            value={h === 'up' && live?.latency_ms != null ? fmtMs(live.latency_ms) : '—'}
            sub="round trip" />
          <Stat bare label="Uptime"
            value={live?.uptime_24h_pct != null ? `${live.uptime_24h_pct}%` : '—'}
            sub="last 24 hours" />
        </StatGroup>
      </div>

      <TrunkRecord trunkId={trunkId} tenantId={tenantId} />
    </div>
  );
}

const RANGES: Array<[number, string]> = [[24, '24 h'], [168, '7 d'], [720, '30 d']];

const STATE_WORD: Record<string, string> = {
  REGISTERED_STATE: 'registered', NOT_REGISTERED_STATE: 'not registered', NOT_LOADED: 'loading',
  REGISTERING_STATE: 'registering', AUTHENTICATING_STATE: 'registering',
  WRONG_CREDENTIALS_STATE: 'login refused', REGISTRAR_ERROR_STATE: 'carrier error',
  REGISTER_TIMEOUT_STATE: 'no answer', INTERNAL_ERROR_STATE: 'switch error',
};
const word = (s: string | null) => (s === null ? '—' : STATE_WORD[s] ?? s);

/**
 * The record: uptime per interval, latency, and every change of state.
 *
 * The same card for staff and customers; only the door differs (the API
 * decides, and adds the carrier's own replies for staff).
 */
export function TrunkRecord({ trunkId, tenantId }: { trunkId: string; tenantId?: string | undefined }) {
  const staff = useSession((s) => s.can)('trunk:read');
  const [hours, setHours] = useState(24);
  const hist = useTrunkHistory({ trunkId, tenantId, staff }, hours);
  const h = hist.data;
  const latency = (h?.buckets ?? []).map((b) => b.latency_ms).filter((v): v is number => v !== null);

  return (
    <div className="arc-card">
      <div className="arc-card-head">
        <h2 className="arc-h2">Record</h2>
        <div className="arc-spacer" />
        <Segmented options={RANGES} value={hours} onChange={setHours} label="Period" />
      </div>
      <div className="arc-card-body">
        {hist.isLoading ? <div className="arc-skeleton" style={{ height: 26 }} />
          : !h || !('buckets' in h) ? <p className="arc-subtle" style={{ margin: 0 }}>No record on this switch.</p>
          : (
            <>
              <UptimeStrip buckets={h.buckets} />
              <div className="arc-row arc-subtle" style={{ fontSize: 11.5, marginTop: 4 }}>
                <span>{RANGES.find(([v]) => v === hours)?.[1]} ago</span>
                <div className="arc-spacer" />
                <span>
                  {h.uptime_pct !== null ? `${h.uptime_pct}% up` : 'no checks'}
                  {h.latency_avg_ms !== null ? ` · ${fmtMs(h.latency_avg_ms)} average, ${fmtMs(h.latency_p95_ms ?? h.latency_avg_ms)} p95` : ''}
                </span>
                <div className="arc-spacer" />
                <span>now</span>
              </div>

              {latency.length >= 2 && (
                <div style={{ marginTop: 14 }}>
                  <div className="arc-label" style={{ marginBottom: 4 }}>Latency</div>
                  <Sparkline values={latency} height={40} label="Latency in milliseconds" />
                </div>
              )}

              <div className="arc-label" style={{ margin: '16px 0 6px' }}>Changes</div>
              {h.events.length === 0 ? (
                <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>None in this period.</p>
              ) : (
                <div className="arc-table-wrap">
                  <table className="arc-table">
                    <tbody>
                      {h.events.map((e) => (
                        <tr key={`${e.at}-${e.kind}`}>
                          <td className="arc-muted arc-num" style={{ whiteSpace: 'nowrap' }}>
                            {new Date(e.at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                          </td>
                          <td>{e.kind === 'health' ? 'Carrier' : 'Registration'}</td>
                          <td>
                            <span className="arc-subtle">{e.kind === 'health' ? e.from_state ?? '—' : word(e.from_state)}</span>
                            {' → '}
                            <StatusBadge status={e.kind === 'health' ? e.to_state
                              : e.to_state === 'REGISTERED_STATE' ? 'registered'
                              : /ERROR|WRONG|TIMEOUT/.test(e.to_state) ? 'unregistered' : 'pending'} />
                            {e.kind === 'registration' && e.to_state !== 'REGISTERED_STATE' && (
                              <span className="arc-subtle"> {word(e.to_state)}</span>
                            )}
                          </td>
                          {e.detail !== undefined && <td className="arc-subtle">{e.detail ?? ''}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
}
